import { supabase } from './supabase';
import { createLogger } from './logger';
import { Session } from '@supabase/supabase-js';

const logger = createLogger({ module: 'fileService' });

// Session caching to reduce auth API calls
let cachedSession: Session | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute cache

/**
 * Get a cached session or fetch a new one if needed
 */
async function getCachedSession(): Promise<Session | null> {
  const now = Date.now();
  
  // Return cached session if still valid
  if (cachedSession && now - cacheTimestamp < CACHE_DURATION) {
    logger.debug('Using cached session');
    return cachedSession;
  }
  
  logger.debug('Fetching new session');
  try {
    const { data } = await supabase.auth.getSession();
    cachedSession = data.session;
    cacheTimestamp = now;
    return cachedSession;
  } catch (error) {
    logger.error('Error fetching session:', error);
    return null;
  }
}

const STORAGE_BUCKETS = {
  WORK_EXPERIENCE: 'work-experience-documents',
  RESUMES: 'resumes'
} as const;

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png'
] as const;

type AllowedMimeType = typeof ALLOWED_MIME_TYPES[number];

export interface FileSource {
  id: string;
  file_name: string;
  file_path?: string;
  mime_type: string;
  source: 'local' | 'google_drive';
  external_file_id?: string;
  external_web_url?: string;
  external_download_url?: string;
  access_token?: string;
  size?: number;
  usage?: {
    type: string;
    work_experience_id?: string;
    context?: string;
  };
  metadata?: {
    display_label?: string;
    [key: string]: string | undefined;
  };
}

export interface UploadWorkExperienceDocumentParams {
  file: File;
  workExperienceId: string;
  displayLabel?: string;
}

export interface UploadResumeParams {
  generateThumbnail?: boolean;
  validateQuota?: boolean;
}

export class FileService {
  private static getStorageBucket(fileType: string): string {
    switch (fileType) {
      case 'work_experience_document':
        return STORAGE_BUCKETS.WORK_EXPERIENCE;
      case 'resume':
        return STORAGE_BUCKETS.RESUMES;
      default:
        return STORAGE_BUCKETS.WORK_EXPERIENCE;
    }
  }

  private static async generateStoragePath(file: File): Promise<string> {
    const session = await getCachedSession();
    if (!session?.user?.id) throw new Error('User must be authenticated to upload files');
    
    const fileExt = file.name.split('.').pop();
    const randomId = Math.random().toString(36).substring(2);
    return `${session.user.id}/${randomId}.${fileExt}`;
  }

  private static validateFile(file: File): void {
    if (!ALLOWED_MIME_TYPES.includes(file.type as AllowedMimeType)) {
      throw new Error(`File type ${file.type} is not supported. Supported types: ${ALLOWED_MIME_TYPES.join(', ')}`);
    }

    // 10MB limit
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error(`File size exceeds limit of ${MAX_SIZE / 1024 / 1024}MB`);
    }
  }

  static async uploadWorkExperienceDocument(params: UploadWorkExperienceDocumentParams): Promise<FileSource> {
    const { file, workExperienceId, displayLabel } = params;

    try {
      // Validate file
      this.validateFile(file);

      // Get user session using cached function
      const session = await getCachedSession();
      if (!session?.user?.id) throw new Error('User must be authenticated to upload files');

      // Generate storage path
      const filePath = await this.generateStoragePath(file);
      const storageBucket = this.getStorageBucket('work_experience_document');

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from(storageBucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { data: fileRecord, error: dbError } = await supabase
        .from('files')
        .insert({
          user_id: session.user.id,
          file_name: file.name,
          file_path: filePath,
          mime_type: file.type,
          size: file.size,
          source: 'local',
          usage: {
            type: 'work_experience_document',
            work_experience_id: workExperienceId,
            context: 'Work Experience Document'
          },
          metadata: {
            display_label: displayLabel || file.name
          }
        })
        .single();

      if (dbError) {
        // Clean up storage if database insert fails
        await supabase.storage
          .from(storageBucket)
          .remove([filePath]);
        throw dbError;
      }

      return fileRecord;
    } catch (error) {
      logger.error('Error uploading work experience document:', error);
      throw error;
    }
  }

  static async getFileUrl(file: FileSource): Promise<string> {
    try {
      switch (file.source) {
        case 'local': {
          if (!file.file_path) {
            throw new Error('File path is required for local files');
          }
          const storageBucket = this.getStorageBucket(file.usage?.type || '');
          const { data, error } = await supabase.storage
            .from(storageBucket)
            .createSignedUrl(file.file_path, 60);

          if (error) throw error;
          return data.signedUrl;
        }

        case 'google_drive': {
          if (!file.external_web_url) {
            throw new Error('External web URL is required for Google Drive files');
          }
          return file.external_web_url;
        }

        default:
          throw new Error(`Unsupported file source: ${file.source}`);
      }
    } catch (error) {
      logger.error('Error getting file URL:', error);
      throw error;
    }
  }

  static async getDownloadUrl(file: FileSource): Promise<string> {
    try {
      switch (file.source) {
        case 'local': {
          if (!file.file_path) {
            throw new Error('File path is required for local files');
          }
          const storageBucket = this.getStorageBucket(file.usage?.type || '');
          const { data, error } = await supabase.storage
            .from(storageBucket)
            .createSignedUrl(file.file_path, 60);

          if (error) throw error;
          return data.signedUrl;
        }

        case 'google_drive': {
          if (!file.external_web_url) {
            throw new Error('External web URL is required for Google Drive files');
          }
          
          logger.debug('Using Google Drive web URL for download:', {
            url: file.external_web_url
          });

          return file.external_web_url;
        }

        default:
          throw new Error(`Unsupported file source: ${file.source}`);
      }
    } catch (error) {
      logger.error('Error getting download URL:', error);
      throw error;
    }
  }

  static async deleteFile(file: FileSource): Promise<void> {
    try {
      // Always soft delete in database
      const { error: dbError } = await supabase
        .from('files')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', file.id);

      if (dbError) throw dbError;

      // Handle storage based on source
      if (file.source === 'local' && file.file_path) {
        const storageBucket = this.getStorageBucket(file.usage?.type || '');
        const { error: storageError } = await supabase.storage
          .from(storageBucket)
          .remove([file.file_path]);

        if (storageError) throw storageError;
      } 
      // Note: We're intentionally not deleting from Google Drive
      // as it could affect other users/systems that might be using the file
      // Instead, we just remove our reference to it
    } catch (error) {
      logger.error('Error deleting file:', error);
      throw error;
    }
  }

  static async updateDisplayLabel(fileId: string, displayLabel: string): Promise<FileSource> {
    try {
      if (!fileId) {
        throw new Error('File ID is required');
      }

      if (!displayLabel) {
        throw new Error('Display label is required');
      }

      logger.debug('Fetching existing file metadata:', { fileId });
      
      // First get the existing file record to preserve metadata
      const { data: existingFile, error: fetchError } = await supabase
        .from('files')
        .select('*')  // Select all fields to ensure the file exists
        .eq('id', fileId)
        .single();

      if (fetchError) {
        logger.error('Error fetching file:', { error: fetchError, fileId });
        throw fetchError;
      }

      if (!existingFile) {
        throw new Error(`File not found with ID: ${fileId}`);
      }

      logger.debug('Updating file metadata:', { 
        fileId, 
        currentMetadata: existingFile.metadata,
        newLabel: displayLabel 
      });

      // Update the file with preserved metadata
      const { data, error } = await supabase
        .from('files')
        .update({
          metadata: {
            ...existingFile.metadata,
            display_label: displayLabel
          }
        })
        .eq('id', fileId)
        .select()  // Add this to return the updated record
        .single();

      if (error) {
        logger.error('Error updating file:', { error, fileId, displayLabel });
        throw error;
      }

      if (!data) {
        throw new Error('Update succeeded but no data was returned');
      }

      logger.debug('Successfully updated file label:', { 
        fileId, 
        newMetadata: data.metadata 
      });

      return data;
    } catch (error) {
      logger.error('Error updating display label:', {
        error,
        fileId,
        displayLabel,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  static async uploadResume(file: File, options: UploadResumeParams = {}): Promise<FileSource> {
    try {
      // Validate file
      this.validateFile(file);

      // Get user session using cached function
      const session = await getCachedSession();
      if (!session?.user?.id) throw new Error('User must be authenticated to upload files');

      // Generate storage path
      const filePath = await this.generateStoragePath(file);
      const storageBucket = this.getStorageBucket('resume');

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from(storageBucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { data: fileRecord, error: dbError } = await supabase
        .from('files')
        .insert({
          user_id: session.user.id,
          file_name: file.name,
          file_path: filePath,
          mime_type: file.type,
          size: file.size,
          source: 'local',
          usage: {
            type: 'resume',
            context: 'Used in Resumes'
          }
        })
        .select()
        .single();

      if (dbError) {
        // Clean up storage if database insert fails
        await supabase.storage
          .from(storageBucket)
          .remove([filePath]);
        throw dbError;
      }

      // For images, use Supabase's image transformation
      if (options.generateThumbnail && file.type.startsWith('image/')) {
        try {
          const { data: urlData } = await supabase.storage
            .from(storageBucket)
            .getPublicUrl(filePath, {
              transform: {
                width: 300,
                height: 300,
                resize: 'contain',
                quality: 80
              }
            });

          if (urlData?.publicUrl) {
            // Update file record with transformed URL
            const { error: updateError } = await supabase
              .from('files')
              .update({
                thumbnail_url: urlData.publicUrl,
                thumbnail_status: 'generated',
                thumbnail_generated_at: new Date().toISOString()
              })
              .eq('id', fileRecord.id);

            if (updateError) {
              logger.error('Error updating thumbnail URL:', updateError);
            }
          }
        } catch (error) {
          logger.error('Error setting up image transformation:', error);
        }
      }

      return fileRecord;
    } catch (error) {
      logger.error('Error uploading resume:', error);
      throw error;
    }
  }
} 